import { Content, ErrorPanel, Header, HeaderLabel, Page } from '@backstage/core-components';
import { discoveryApiRef, fetchApiRef, useApi } from '@backstage/core-plugin-api';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useEffect, useMemo, useState } from 'react';

import {
  filterRepoRows,
  getDefaultRepoTableFilters,
  SonarRepoTable,
} from '../../repo/SonarRepoTable';
import {
  CLASSIFICATIONS,
  DEFAULT_GROUP_BY,
  GROUP_BY_API_PARAMS,
  GROUP_BY_OPTIONS,
  METRICS,
  METRIC_KEYS,
  REPOSITORY_GROUP_BY,
} from '../constants';
import { ButtonPill, CoverageCell, KpiCard, KpiTrendLine, QualityCell } from './SonarMetricPrimitives';
import { SonarLatestRankingCard } from './SonarLatestRankingCard';
import { SonarTimeSeriesChart } from './SonarTimeSeriesChart';
import { MetricsBreadcrumbs } from '../../../MetricsBreadcrumbs';
import { GroupBy, KpiSeriesKey, KpiSeriesMap, MetricKey, RepoRow, TeamRow, TimeSeriesPoint, TribeRow } from '../types';
import {
  average,
  coverageColor,
  daysAgoInputValue,
  formatDate,
  formatFullCount,
  getRiskReason,
  getRiskScore,
  getSnapshotComparison,
  hasHighRiskRating,
  isLowCoverageItem,
  metricColor,
  ratingColor,
  ratingLabel,
  toDateInputValue,
  toNumber,
} from '../utils';

type DashboardScope = 'repository' | 'team' | 'tribe';
type SnapshotRow = RepoRow | TeamRow | TribeRow;

interface SonarScopedDashboardPageProps {
  initialScope: DashboardScope;
}

const SCOPE_OPTIONS: Array<{ key: DashboardScope; label: string }> = [
  { key: 'repository', label: 'Repository view' },
  { key: 'team', label: 'Team view' },
  { key: 'tribe', label: 'Tribe view' },
];

const TEAM_GROUP_BY_OPTIONS = GROUP_BY_OPTIONS.filter(
  option => option.key !== REPOSITORY_GROUP_BY,
);
const TRIBE_GROUP_BY_OPTIONS = GROUP_BY_OPTIONS.filter(
  option => option.key === 'tribe',
);

const styles = {
  shell: {
    position: 'relative' as const,
    background: '#f6f8fb',
    padding: 20,
    borderRadius: 18,
    maxWidth: '100%',
    overflowX: 'hidden' as const,
    boxSizing: 'border-box' as const,
  },
  hero: {
    background: 'linear-gradient(135deg, #0f2a56 0%, #174ea6 55%, #2563eb 100%)',
    color: '#ffffff',
    borderRadius: 24,
    padding: 24,
    overflow: 'hidden' as const,
    boxSizing: 'border-box' as const,
    boxShadow: '0 18px 42px rgba(15, 42, 86, 0.22)',
  },
  card: {
    borderRadius: 20,
    border: '1px solid #e2e8f0',
    boxShadow: '0 12px 28px rgba(15, 23, 42, 0.06)',
    overflow: 'hidden' as const,
    maxWidth: '100%',
    boxSizing: 'border-box' as const,
  },
  tableHeader: {
    background: '#f1f5f9',
    color: '#334155',
    fontWeight: 900,
    fontSize: 12,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.4,
  },
};

const useStyles = makeStyles({
  tableScroller: {
    maxHeight: 520,
    overflow: 'auto',
    maxWidth: '100%',
    borderRadius: 18,
    border: '1px solid #dbe3ef',
    boxShadow: 'none',
    background: '#ffffff',
    scrollbarWidth: 'thin',
    scrollbarColor: '#cbd5e1 #f1f5f9',
    '&::-webkit-scrollbar': {
      width: 10,
      height: 10,
      backgroundColor: '#f1f5f9',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: '#f1f5f9',
      borderRadius: 8,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#cbd5e1',
      borderRadius: 8,
      border: '2px solid #f1f5f9',
    },
    '&::-webkit-scrollbar-thumb:hover': {
      backgroundColor: '#94a3b8',
    },
    '&::-webkit-scrollbar-corner': {
      backgroundColor: '#f1f5f9',
    },
  },
});

const TeamTable = ({ rows }: { rows: TeamRow[] }) => {
  const classes = useStyles();
  if (!rows.length) {
    return (
      <Typography style={{ padding: 18, color: '#64748b' }}>
        No team data found for this classification.
      </Typography>
    );
  }

  return (
    <TableContainer
      component={Paper}
      className={classes.tableScroller}
    >
      <Table size="small" stickyHeader style={{ minWidth: 1040, tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ ...styles.tableHeader, width: 240, whiteSpace: 'nowrap', padding: '13px 18px' }}>Team</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 160, whiteSpace: 'nowrap', padding: '13px 16px' }}>Tribe</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 105, whiteSpace: 'nowrap', padding: '13px 12px' }}>Updated</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 185, whiteSpace: 'nowrap', padding: '13px 16px' }}>Coverage</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 300, whiteSpace: 'nowrap', padding: '13px 16px' }}>Quality</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 150, whiteSpace: 'nowrap', padding: '13px 20px' }} align="right">Smells</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const coverage = toNumber(row.coverage);
            const reliability = toNumber(row.reliability);
            const security = toNumber(row.security);
            const maintainability = toNumber(row.maintainability);

            return (
              <TableRow key={`${row.team}-${row.tribe}-${row.sno}-${index}`} hover style={{ background: index % 2 === 0 ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #eef2f7' }}>
                <TableCell style={{ padding: '15px 18px', verticalAlign: 'middle' }}>
                  <Tooltip title={row.team || '-'}>
                    <Typography style={{ color: '#0f172a', fontWeight: 900, maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15 }}>
                      {row.team || '-'}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell style={{ padding: '15px 16px', verticalAlign: 'middle' }}>
                  <Typography style={{ color: '#1e293b', fontWeight: 800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 14 }}>
                    {row.tribe || '-'}
                  </Typography>
                </TableCell>

                <TableCell style={{ padding: '15px 12px', verticalAlign: 'middle' }}>
                  <Chip size="small" label={formatDate(row.date)} style={{ background: '#f1f5f9', color: '#475569', fontWeight: 850, border: '1px solid #e2e8f0' }} />
                </TableCell>

                <TableCell style={{ padding: '15px 16px', verticalAlign: 'middle' }}>
                  <CoverageCell value={coverage} />
                </TableCell>

                <TableCell style={{ padding: '15px 16px', verticalAlign: 'middle' }}>
                  <QualityCell reliability={reliability} security={security} maintainability={maintainability} />
                </TableCell>

                <TableCell align="right" style={{ padding: '15px 18px', verticalAlign: 'middle' }}>
                  <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 112, maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 950, color: Number(row.smells || 0) > 250 ? '#b45309' : '#0f172a', fontSize: 15, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                    {formatFullCount(toNumber(row.smells))}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const TribeTable = ({ rows }: { rows: TribeRow[] }) => {
  const classes = useStyles();
  if (!rows.length) {
    return (
      <Typography style={{ padding: 18, color: '#64748b' }}>
        No tribe data found for this classification.
      </Typography>
    );
  }

  return (
    <TableContainer
      component={Paper}
      className={classes.tableScroller}
    >
      <Table size="small" stickyHeader style={{ minWidth: 980, tableLayout: 'fixed' }}>
        <TableHead>
          <TableRow>
            <TableCell style={{ ...styles.tableHeader, width: 260, whiteSpace: 'nowrap', padding: '13px 18px' }}>Tribe</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 120, whiteSpace: 'nowrap', padding: '13px 12px' }}>Updated</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 185, whiteSpace: 'nowrap', padding: '13px 16px' }}>Coverage</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 300, whiteSpace: 'nowrap', padding: '13px 16px' }}>Quality</TableCell>
            <TableCell style={{ ...styles.tableHeader, width: 150, whiteSpace: 'nowrap', padding: '13px 20px' }} align="right">Smells</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, index) => {
            const coverage = toNumber(row.coverage);
            const reliability = toNumber(row.reliability);
            const security = toNumber(row.security);
            const maintainability = toNumber(row.maintainability);

            return (
              <TableRow key={`${row.tribe}-${row.sno}-${index}`} hover style={{ background: index % 2 === 0 ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #eef2f7' }}>
                <TableCell style={{ padding: '15px 18px', verticalAlign: 'middle' }}>
                  <Tooltip title={row.tribe || '-'}>
                    <Typography style={{ color: '#0f172a', fontWeight: 900, maxWidth: 230, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 15 }}>
                      {row.tribe || '-'}
                    </Typography>
                  </Tooltip>
                </TableCell>

                <TableCell style={{ padding: '15px 12px', verticalAlign: 'middle' }}>
                  <Chip size="small" label={formatDate(row.date)} style={{ background: '#f1f5f9', color: '#475569', fontWeight: 850, border: '1px solid #e2e8f0' }} />
                </TableCell>

                <TableCell style={{ padding: '15px 16px', verticalAlign: 'middle' }}>
                  <CoverageCell value={coverage} />
                </TableCell>

                <TableCell style={{ padding: '15px 16px', verticalAlign: 'middle' }}>
                  <QualityCell reliability={reliability} security={security} maintainability={maintainability} />
                </TableCell>

                <TableCell align="right" style={{ padding: '15px 18px', verticalAlign: 'middle' }}>
                  <Box component="span" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', minWidth: 112, maxWidth: 150, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 950, color: Number(row.smells || 0) > 250 ? '#b45309' : '#0f172a', fontSize: 15, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>
                    {formatFullCount(toNumber(row.smells))}
                  </Box>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const getDefaultGroupBy = (scope: DashboardScope): GroupBy => {
  switch (scope) {
    case 'repository':
      return DEFAULT_GROUP_BY;
    case 'team':
      return 'team';
    case 'tribe':
      return 'tribe';
  }
};

const getAvailableGroupByOptions = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return GROUP_BY_OPTIONS;
    case 'team':
      return TEAM_GROUP_BY_OPTIONS;
    case 'tribe':
      return TRIBE_GROUP_BY_OPTIONS;
  }
};

const getScopePageTitle = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Sonar Metrics - Repo View';
    case 'team':
      return 'Sonar Metrics - Team View';
    case 'tribe':
      return 'Sonar Metrics - Tribe View';
  }
};

const getScopeHeaderValue = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Repository View';
    case 'team':
      return 'Team View';
    case 'tribe':
      return 'Tribe View';
  }
};

const getControlsDescription = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Choose the dashboard scope and what the main trend should show. Group By and the graph date range affect the trend sections only.';
    case 'team':
      return 'Choose the dashboard scope and what the trend should show. Group By and the graph date range affect the trend sections only.';
    case 'tribe':
      return 'Choose the dashboard scope and what the tribe trend should show. Group By and the graph date range affect the trend sections only.';
  }
};

const getLoadingLabel = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Loading repository metrics...';
    case 'team':
      return 'Loading team metrics...';
    case 'tribe':
      return 'Loading tribe metrics...';
  }
};

const getSnapshotEndpoint = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'repos';
    case 'team':
      return 'teams';
    case 'tribe':
      return 'tribes/view';
  }
};

const getSelectedLabel = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Selected repos';
    case 'team':
      return 'Selected teams';
    case 'tribe':
      return 'Selected tribes';
  }
};

const getSelectedHelper = (
  scope: DashboardScope,
  relatedCount: number,
  classification: string,
) => {
  switch (scope) {
    case 'repository':
      return `${relatedCount} teams in ${classification}`;
    case 'team':
      return `${relatedCount} tribes in ${classification}`;
    case 'tribe':
      return `Latest tribe snapshot for ${classification}`;
  }
};

const getSecondaryCount = (rows: SnapshotRow[], scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return new Set((rows as RepoRow[]).map(row => row.team).filter(Boolean)).size;
    case 'team':
      return new Set((rows as TeamRow[]).map(row => row.tribe).filter(Boolean)).size;
    case 'tribe':
      return 0;
  }
};

const getAverageCoverageHelper = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Across selected repos in latest snapshot';
    case 'team':
      return 'Across selected teams in latest snapshot';
    case 'tribe':
      return 'Across selected tribes in latest snapshot';
  }
};



const getHighRiskLabel = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'High risk repos';
    case 'team':
      return 'High risk teams';
    case 'tribe':
      return 'High risk tribes';
  }
};

const getHighRiskTitle = (scope: DashboardScope, count: number) => {
  switch (scope) {
    case 'repository':
      return `${count.toLocaleString()} repositories have at least one D/E quality rating`;
    case 'team':
      return `${count.toLocaleString()} teams have at least one D/E quality rating`;
    case 'tribe':
      return `${count.toLocaleString()} tribes have at least one D/E quality rating`;
  }
};

const getLowCoverageLabel = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Low coverage repos';
    case 'team':
      return 'Low coverage teams';
    case 'tribe':
      return 'Low coverage tribes';
  }
};

const getLowCoverageTitle = (scope: DashboardScope, count: number) => {
  switch (scope) {
    case 'repository':
      return `${count.toLocaleString()} repositories have coverage below 80%`;
    case 'team':
      return `${count.toLocaleString()} teams have coverage below 80%`;
    case 'tribe':
      return `${count.toLocaleString()} tribes have coverage below 80%`;
  }
};

const getTopRiskLabel = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Top risk repo';
    case 'team':
      return 'Top risk team';
    case 'tribe':
      return 'Top risk tribe';
  }
};

const getTopRiskEmptyCopy = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'No repository data';
    case 'team':
      return 'No team data';
    case 'tribe':
      return 'No tribe data';
  }
};

const getTopRiskName = (row: SnapshotRow, scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return (row as RepoRow).name;
    case 'team':
      return (row as TeamRow).team;
    case 'tribe':
      return (row as TribeRow).tribe;
  }
};

const getTopRiskColor = (row: SnapshotRow, scope: DashboardScope) =>
  scope === 'repository'
    ? metricColor(toNumber(row.coverage), 'coverage')
    : coverageColor(toNumber(row.coverage));



const getDetailsTitle = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Repository details';
    case 'team':
      return 'Team details';
    case 'tribe':
      return 'Tribe details';
  }
};

const getDetailsDescription = (scope: DashboardScope) => {
  switch (scope) {
    case 'repository':
      return 'Latest snapshot of Repositories with code quality metrics.';
    case 'team':
      return 'Latest snapshot of Teams with code quality metrics.';
    case 'tribe':
      return 'Latest snapshot of Tribes with code quality metrics.';
  }
};

const getDetailsChipLabel = (scope: DashboardScope, count: number) => {
  switch (scope) {
    case 'repository':
      return `${count} repositories`;
    case 'team':
      return `${count} teams`;
    case 'tribe':
      return `${count} tribes`;
  }
};

const getTrendTitle = (
  _scope: DashboardScope,
  activeClassification: string,
  selectedGroupLabel: string,
) => `${activeClassification} ${selectedGroupLabel.toLowerCase()} trend`;

const getKpiRequests = (scope: DashboardScope, groupBy: GroupBy) => {
  if (scope === 'repository') {
    return [
      { key: 'repo_count' as KpiSeriesKey, metric: 'coverage' as MetricKey, groupBy: REPOSITORY_GROUP_BY },
      { key: 'coverage' as KpiSeriesKey, metric: 'coverage' as MetricKey, groupBy: REPOSITORY_GROUP_BY },
      { key: 'reliability_rating' as KpiSeriesKey, metric: 'reliability_rating' as MetricKey, groupBy: REPOSITORY_GROUP_BY },
    ];
  }

  return [
    { key: 'coverage' as KpiSeriesKey, metric: 'coverage' as MetricKey, groupBy },
    { key: 'reliability_rating' as KpiSeriesKey, metric: 'reliability_rating' as MetricKey, groupBy },
  ];
};

export const SonarScopedDashboardPage = ({ initialScope }: SonarScopedDashboardPageProps) => {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const [scope, setScope] = useState<DashboardScope>(initialScope);
  const [baseUrl, setBaseUrl] = useState<string | null>(null);
  const [snapshotData, setSnapshotData] = useState<Record<string, SnapshotRow[]>>({});
  const [timeSeriesData, setTimeSeriesData] = useState<TimeSeriesPoint[]>([]);
  const [kpiSeriesData, setKpiSeriesData] = useState<KpiSeriesMap>({});
  const [loadingKpiSeries, setLoadingKpiSeries] = useState(false);
  const [activeClassification, setActiveClassification] = useState<(typeof CLASSIFICATIONS)[number]>('Active');
  const [metric, setMetric] = useState<MetricKey>('coverage');
  const [groupBy, setGroupBy] = useState<GroupBy>(getDefaultGroupBy(initialScope));
  const [repoFilters, setRepoFilters] = useState(getDefaultRepoTableFilters);
  const [fromDate, setFromDate] = useState(daysAgoInputValue(7));
  const [toDate, setToDate] = useState(toDateInputValue(new Date()));
  const [loadingSnapshot, setLoadingSnapshot] = useState(true);
  const [loadingSeries, setLoadingSeries] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const availableGroupByOptions = useMemo(
    () => getAvailableGroupByOptions(scope),
    [scope],
  );

  const effectiveGroupBy = useMemo(() => {
    const allowedKeys = new Set(availableGroupByOptions.map(option => option.key));
    return allowedKeys.has(groupBy) ? groupBy : getDefaultGroupBy(scope);
  }, [availableGroupByOptions, groupBy, scope]);

  const selectedGroupLabel =
    availableGroupByOptions.find(item => item.key === effectiveGroupBy)?.label ?? 'Tribe';

  useEffect(() => {
    setGroupBy(getDefaultGroupBy(scope));
  }, [scope]);

  useEffect(() => {
    if (groupBy !== effectiveGroupBy) {
      setGroupBy(effectiveGroupBy);
    }
  }, [effectiveGroupBy, groupBy]);

  useEffect(() => {
    const loadSnapshot = async () => {
      try {
        setLoadingSnapshot(true);
        setError(null);
        const discoveredBaseUrl = await discoveryApi.getBaseUrl('metrics');
        setBaseUrl(discoveredBaseUrl);

        const endpoint = getSnapshotEndpoint(scope);
        const result: Record<string, SnapshotRow[]> = {};

        for (const classification of CLASSIFICATIONS) {
          const response = await fetchApi.fetch(
            `${discoveredBaseUrl}/${endpoint}?classification=${encodeURIComponent(classification)}`,
          );
          result[classification] = response.ok ? await response.json() : [];
        }

        setSnapshotData(result);
      } catch (e) {
        setError(
          e instanceof Error
            ? e.message
            : `Failed to load ${scope} metrics`,
        );
      } finally {
        setLoadingSnapshot(false);
      }
    };

    loadSnapshot();
  }, [discoveryApi, fetchApi, scope]);

  useEffect(() => {
    const loadTimeSeries = async () => {
      if (!baseUrl) return;

      try {
        setLoadingSeries(true);
        const params = new URLSearchParams({
          groupBy: GROUP_BY_API_PARAMS[effectiveGroupBy],
          metric,
          classification: activeClassification,
        });

        if (fromDate) params.set('from', fromDate);
        if (toDate) params.set('to', toDate);

        const response = await fetchApi.fetch(`${baseUrl}/timeseries?${params.toString()}`);
        setTimeSeriesData(response.ok ? await response.json() : []);
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Failed to load time-series data');
      } finally {
        setLoadingSeries(false);
      }
    };

    loadTimeSeries();
  }, [activeClassification, baseUrl, effectiveGroupBy, fetchApi, fromDate, metric, toDate]);

  useEffect(() => {
    const loadKpiSeries = async () => {
      if (!baseUrl) return;

      try {
        setLoadingKpiSeries(true);
        const requests = getKpiRequests(scope, effectiveGroupBy);
        const snapshotFromDate = daysAgoInputValue(1);
        const snapshotToDate = toDateInputValue(new Date());

        const entries = await Promise.all(
          requests.map(async item => {
            const params = new URLSearchParams({
              groupBy: GROUP_BY_API_PARAMS[item.groupBy],
              metric: item.metric,
              classification: activeClassification,
              from: snapshotFromDate,
              to: snapshotToDate,
            });

            const response = await fetchApi.fetch(`${baseUrl}/timeseries?${params.toString()}`);
            return [item.key, response.ok ? await response.json() : []] as [KpiSeriesKey, TimeSeriesPoint[]];
          }),
        );

        setKpiSeriesData(Object.fromEntries(entries));
      } catch {
        setKpiSeriesData({});
      } finally {
        setLoadingKpiSeries(false);
      }
    };

    loadKpiSeries();
  }, [activeClassification, baseUrl, effectiveGroupBy, fetchApi, scope]);

  const activeRows = snapshotData[activeClassification] || [];
  const repositoryRows = activeRows as RepoRow[];

  const detailRows = useMemo(() => {
    if (scope !== 'repository') {
      return activeRows;
    }

    return filterRepoRows(repositoryRows, repoFilters);
  }, [activeRows, repoFilters, repositoryRows, scope]);

  // Keep graph scoped to the active snapshot, independent of local table filters.
  const chartScopeRows = activeRows;

  const scopedTimeSeriesData = useMemo(() => {
    const allowedGroups = new Set(
      chartScopeRows
        .map(row => {
          if (effectiveGroupBy === 'tribe') {
            return row.tribe;
          }

          if (effectiveGroupBy === 'team' && 'team' in row) {
            return row.team;
          }

          if (effectiveGroupBy === 'repository' && 'name' in row) {
            return row.name;
          }

          return undefined;
        })
        .filter((value): value is string => Boolean(value)),
    );

    if (!allowedGroups.size) {
      return [];
    }

    return timeSeriesData.filter(point => allowedGroups.has(point.metric));
  }, [chartScopeRows, effectiveGroupBy, timeSeriesData]);

  const stats = useMemo(() => {
    const avgCoverage = average(activeRows.map(row => row.coverage));
    const avgReliability = average(activeRows.map(row => row.reliability));
    const relatedCount = getSecondaryCount(activeRows, scope);
    const highRiskCount = activeRows.filter(hasHighRiskRating).length;
    const lowCoverageCount = activeRows.filter(isLowCoverageItem).length;
    const topRiskItem = activeRows.length
      ? [...activeRows].sort((a, b) => getRiskScore(b) - getRiskScore(a))[0]
      : null;

    return {
      avgCoverage,
      avgReliability,
      relatedCount,
      highRiskCount,
      lowCoverageCount,
      topRiskItem,
    };
  }, [activeRows, scope]);

  const kpiComparisons = useMemo(
    () => ({
      repoCount:
        scope === 'repository'
          ? getSnapshotComparison(kpiSeriesData.repo_count || [], 'count')
          : null,
      coverage: getSnapshotComparison(kpiSeriesData.coverage || [], 'average'),
      reliability: getSnapshotComparison(kpiSeriesData.reliability_rating || [], 'average'),
    }),
    [kpiSeriesData, scope],
  );

  const controlsDisabled = loadingSnapshot || loadingSeries || loadingKpiSeries;

  const hasSnapshotData = Object.keys(snapshotData).length > 0;
  const showInitialLoading = loadingSnapshot && !hasSnapshotData;
  const showInlineSnapshotLoading = loadingSnapshot && hasSnapshotData;

  if (showInitialLoading) {
    return (
      <Page themeId="tool">
        <Header title={getScopePageTitle(scope)}>
          <HeaderLabel label="Dashboard" value={getScopeHeaderValue(scope)} />
        </Header>
        <Content>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={360} flexDirection="column">
            <CircularProgress />
            <Typography style={{ marginTop: 16, color: '#64748b', fontWeight: 700 }}>
              {getLoadingLabel(scope)}
            </Typography>
          </Box>
        </Content>
      </Page>
    );
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Header title={getScopePageTitle(scope)} />
        <Content>
          <ErrorPanel error={new Error(error)} />
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Content>
        <Box style={styles.shell}>
          <MetricsBreadcrumbs currentPage="Code Quality Metrics View" />
          {/* <Box style={styles.hero}>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={7}>
                <Typography style={{ fontSize: 29, fontWeight: 950, lineHeight: 1.14 }}>
                  {getHeroTitle(scope)}
                </Typography>
                <Typography style={{ color: '#dbeafe', fontSize: 15, marginTop: 10, maxWidth: 760 }}>
                  {getHeroDescription(scope)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={5}>
                <Box style={{ background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.22)', borderRadius: 18, padding: 18 }}>
                  <Typography style={{ color: '#bfdbfe', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    Current view
                  </Typography>
                  <Typography style={{ color: '#ffffff', fontSize: 17, fontWeight: 900, marginTop: 6 }}>
                    {activeClassification} latest snapshot
                  </Typography>
                  <Typography style={{ color: '#dbeafe', fontSize: 13, marginTop: 6 }}>
                    {getCurrentViewDescription(scope)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box> */}

          <Box mt={3}  style={styles.hero}>
            <Card style={styles.card}>
              <CardContent style={{ padding: 20 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <Typography style={{ color: '#0f172a', fontWeight: 950, fontSize: 19 }}>
                      Dashboard controls
                    </Typography>
                    <Typography style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
                      {getControlsDescription(scope)}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={8}>
                    <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ gap: 12 }}>
                      <Box>
                        <Typography style={{ color: '#334155', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', marginBottom: 8, textAlign: 'right' }}>
                          View
                        </Typography>
                        <Box display="flex" flexWrap="wrap" justifyContent="flex-end" alignItems="center" style={{ gap: 8 }}>
                          {SCOPE_OPTIONS.map(option => (
                            <ButtonPill
                              key={option.key}
                              active={scope === option.key}
                              label={option.label}
                              onClick={() => setScope(option.key)}
                              disabled={controlsDisabled}
                            />
                          ))}
                        </Box>
                      </Box>
                      <Box display="flex" flexWrap="wrap" justifyContent="flex-end" style={{ gap: 8 }}>
                        {CLASSIFICATIONS.map(classification => (
                          <ButtonPill
                            key={classification}
                            active={activeClassification === classification}
                            label={`${classification} (${snapshotData[classification]?.length || 0})`}
                            onClick={() => setActiveClassification(classification)}
                            disabled={controlsDisabled}
                          />
                        ))}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Box>

          <Box
            style={{
              position: 'relative',
              marginTop: 8,
              transition: 'opacity 180ms ease, transform 180ms ease, filter 180ms ease',
              opacity: showInlineSnapshotLoading ? 0.62 : 1,
              transform: showInlineSnapshotLoading ? 'translateY(4px)' : undefined,
              filter: showInlineSnapshotLoading ? 'saturate(0.88)' : undefined,
              pointerEvents: showInlineSnapshotLoading ? 'none' : 'auto',
            }}
          >
            {showInlineSnapshotLoading && (
              <Box
                style={{
                  position: 'absolute',
                  top: 14,
                  left: 0,
                  right: 0,
                  zIndex: 4,
                  display: 'flex',
                  justifyContent: 'center',
                  pointerEvents: 'none',
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  style={{
                    gap: 10,
                    background: 'rgba(15, 23, 42, 0.92)',
                    color: '#ffffff',
                    borderRadius: 999,
                    padding: '10px 16px',
                    boxShadow: '0 16px 32px rgba(15, 23, 42, 0.18)',
                  }}
                >
                  <CircularProgress size={18} style={{ color: '#ffffff' }} />
                  <Typography style={{ fontSize: 13, fontWeight: 900, letterSpacing: 0.2 }}>
                    Updating dashboard data...
                  </Typography>
                </Box>
              </Box>
            )}

            <Grid container spacing={2} style={{ marginTop: 0 }}>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  label={getSelectedLabel(scope)}
                  value={activeRows.length.toString()}
                  helper={getSelectedHelper(scope, stats.relatedCount, activeClassification)}
                  color="#2563eb"
                  trend={
                    scope === 'repository' && !loadingKpiSeries && kpiComparisons.repoCount ? (
                      <KpiTrendLine comparison={kpiComparisons.repoCount} seriesKey="repo_count" />
                    ) : undefined
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  label="Average coverage"
                  value={stats.avgCoverage !== null ? `${stats.avgCoverage.toFixed(1)}%` : '-'}
                  helper={getAverageCoverageHelper(scope)}
                  color={coverageColor(stats.avgCoverage)}
                  trend={!loadingKpiSeries && <KpiTrendLine comparison={kpiComparisons.coverage} seriesKey="coverage" />}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  label="Reliability"
                  value={stats.avgReliability !== null ? ratingLabel(stats.avgReliability) : '-'}
                  helper={stats.avgReliability !== null ? `Average rating ${stats.avgReliability.toFixed(2)}` : 'No rating data'}
                  color={ratingColor(stats.avgReliability)}
                  trend={!loadingKpiSeries && <KpiTrendLine comparison={kpiComparisons.reliability} seriesKey="reliability_rating" />}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  label={getHighRiskLabel(scope)}
                  value={stats.highRiskCount.toLocaleString()}
                  helper="D/E in reliability, security, or maintainability"
                  color={stats.highRiskCount > 0 ? '#dc2626' : '#16a34a'}
                  title={getHighRiskTitle(scope, stats.highRiskCount)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  label={getLowCoverageLabel(scope)}
                  value={stats.lowCoverageCount.toLocaleString()}
                  helper="Coverage below 80% in latest snapshot"
                  color={stats.lowCoverageCount > 0 ? '#f97316' : '#16a34a'}
                  title={getLowCoverageTitle(scope, stats.lowCoverageCount)}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <KpiCard
                  label={getTopRiskLabel(scope)}
                  value={stats.topRiskItem ? getTopRiskName(stats.topRiskItem, scope) : '-'}
                  helper={stats.topRiskItem ? getRiskReason(stats.topRiskItem) : getTopRiskEmptyCopy(scope)}
                  color={stats.topRiskItem ? getTopRiskColor(stats.topRiskItem, scope) : '#94a3b8'}
                  title={stats.topRiskItem ? `${getTopRiskName(stats.topRiskItem, scope)} | ${getRiskReason(stats.topRiskItem)} | score ${getRiskScore(stats.topRiskItem).toFixed(1)}` : getTopRiskEmptyCopy(scope)}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} style={{ marginTop: 8 }}>
              <Grid item xs={12} lg={8}>
                <Card style={styles.card}>
                  <CardContent style={{ padding: 20 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" style={{ gap: 16 }}>
                      <Box>
                        <Typography style={{ color: '#0f172a', fontWeight: 950, fontSize: 22 }}>
                          {getTrendTitle(scope, activeClassification, selectedGroupLabel)}
                        </Typography>
                        <Typography style={{ color: '#64748b', marginTop: 4 }}>
                          Default range is the last 7 days; change From and To to inspect another period.
                        </Typography>
                      </Box>
                      {loadingSeries && <CircularProgress size={24} />}
                    </Box>

                    <Divider style={{ margin: '18px 0' }} />

                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={5}>
                        <Typography style={{ color: '#334155', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', marginBottom: 8 }}>
                          Metric
                        </Typography>
                        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                          {METRIC_KEYS.map(item => (
                            <ButtonPill
                              key={item}
                              active={metric === item}
                              label={METRICS[item].short}
                              onClick={() => setMetric(item)}
                            />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography style={{ color: '#334155', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', marginBottom: 8 }}>
                          Group by
                        </Typography>
                        <Box display="flex" flexWrap="wrap" style={{ gap: 8 }}>
                          {availableGroupByOptions.map(item => (
                            <ButtonPill
                              key={item.key}
                              active={effectiveGroupBy === item.key}
                              label={item.label}
                              onClick={() => setGroupBy(item.key)}
                            />
                          ))}
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={4}>
                        <Typography style={{ color: '#334155', fontSize: 12, fontWeight: 900, textTransform: 'uppercase', marginBottom: 8 }}>
                          Graph date range
                        </Typography>
                        <Box display="flex" flexWrap="wrap" style={{ gap: 10 }}>
                          <TextField
                            label="From"
                            type="date"
                            size="small"
                            variant="outlined"
                            value={fromDate}
                            onChange={event => setFromDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            style={{ minWidth: 150, flex: '1 1 150px' }}
                          />
                          <TextField
                            label="To"
                            type="date"
                            size="small"
                            variant="outlined"
                            value={toDate}
                            onChange={event => setToDate(event.target.value)}
                            InputLabelProps={{ shrink: true }}
                            style={{ minWidth: 150, flex: '1 1 150px' }}
                          />
                        </Box>
                      </Grid>
                    </Grid>

                    <Box mt={3}>
                      {loadingSeries ? (
                        <Box minHeight={420} display="flex" alignItems="center" justifyContent="center">
                          <CircularProgress />
                        </Box>
                      ) : (
                        <SonarTimeSeriesChart
                          data={scopedTimeSeriesData}
                          metric={metric}
                          fromDate={fromDate}
                          toDate={toDate}
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} lg={4}>
                <SonarLatestRankingCard data={scopedTimeSeriesData} metric={metric} />
              </Grid>

              <Grid item xs={12}>
                <Card style={styles.card}>
                  <CardContent style={{ padding: 20 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" mb={2} style={{ gap: 12 }}>
                      <Box>
                        <Typography style={{ color: '#0f172a', fontWeight: 950, fontSize: 20 }}>
                          {getDetailsTitle(scope)}
                        </Typography>
                        <Typography style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
                          {getDetailsDescription(scope)}
                        </Typography>
                      </Box>
                      <Chip label={getDetailsChipLabel(scope, detailRows.length)} style={{ background: '#eff6ff', color: '#1d4ed8', fontWeight: 900 }} />
                    </Box>
                    {scope === 'repository' ? (
                      <SonarRepoTable
                        rows={repositoryRows}
                        filters={repoFilters}
                        onFiltersChange={setRepoFilters}
                      />
                    ) : scope === 'team' ? (
                      <TeamTable rows={activeRows as TeamRow[]} />
                    ) : (
                      <TribeTable rows={activeRows as TribeRow[]} />
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Content>
    </Page>
  );
};